import { createTheme } from '@mui/material/styles';
import { palette } from './palette';
import { typography } from './typography';
import { components } from './components';

/**
 * Main theme configuration for the Kaive application
 *
 * This theme is used by MUI's ThemeProvider to style all components.
 * Update palette.ts when you have your brand colors ready.
 *
 * Usage in your app:
 * ```tsx
 * import { ThemeProvider } from '@mui/material/styles';
 * import { theme } from './v2/theme';
 *
 * function App() {
 *   return (
 *     <ThemeProvider theme={theme}>
 *       <YourApp />
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */
export const theme = createTheme({
  // Color palette
  palette,

  // Typography settings
  typography,

  // Component customizations
  components,

  // Spacing - base unit is 8px
  // theme.spacing(1) = 8px
  // theme.spacing(2) = 16px
  // theme.spacing(3) = 24px, etc.
  spacing: 8,

  // Responsive breakpoints
  breakpoints: {
    values: {
      xs: 0,        // Phone (portrait)
      sm: 600,      // Phone (landscape) / Tablet (portrait)
      md: 960,      // Tablet (landscape) / Small laptop
      lg: 1280,     // Desktop
      xl: 1920,     // Large desktop
    },
  },

  // Shape settings
  shape: {
    borderRadius: 8,  // Default border radius for components
  },

  // Shadow settings (0-24, with 0 being no shadow)
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.10)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.14)',
    '0px 20px 40px rgba(0, 0, 0, 0.16)',
    '0px 24px 48px rgba(0, 0, 0, 0.18)',
    '0px 28px 56px rgba(0, 0, 0, 0.20)',
    // ... MUI requires 25 shadow levels
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.10)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.14)',
    '0px 20px 40px rgba(0, 0, 0, 0.16)',
    '0px 24px 48px rgba(0, 0, 0, 0.18)',
    '0px 28px 56px rgba(0, 0, 0, 0.20)',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.10)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.14)',
    '0px 20px 40px rgba(0, 0, 0, 0.16)',
    '0px 24px 48px rgba(0, 0, 0, 0.18)',
    '0px 28px 56px rgba(0, 0, 0, 0.20)',
  ],

  // Z-index levels (for layering components)
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
});

export default theme;
